import React, { useEffect, useReducer, useRef } from 'react';
import SolarGoogleMap from '../SolarMap/SolarGoogleMap';
import { Alert, Button, Stack, Typography, Container } from "@mui/material";
import { Form } from "@base-ui-components/react";
import { useText } from "../../main.tsx";
import type { BuildingInsightsResponse } from "../SolarMap/SolarAPI/solar.ts";
import { trackSolarMapContinued } from "../../utils/funnelTracking.ts";
import type { Coordinates } from "../../types/solar";
import {
    initialSolarMapStepState,
    solarMapStepReducer,
} from "./SolarMapStep.reducer";
import { useAddressData, useFunnelStep } from "../../context/FunnelDataContext";
import { FUNNEL_STEP_IDS } from "../../constants/funnelSteps";

export type SolarMapStepData = {
    buildingInsights: BuildingInsightsResponse,
    markerPosition: {
        coordinates: Coordinates,
        doesMarkerEqualsLocation: boolean
    }
};

const SolarMapStep: React.FC = () => {
    const { data: stepData, update: onUpdate, submit: onSubmit } =
        useFunnelStep<SolarMapStepData>(FUNNEL_STEP_IDS.PRODUCT_SELECTION);
    const address = useAddressData()!.currentAddress;
    const location = address.location!;
    const [state, dispatch] = useReducer(
        solarMapStepReducer,
        {
            ...initialSolarMapStepState(stepData, {
                coordinates: location,
                doesMarkerEqualsLocation: false,
            }),
        }
    );
    const onUpdateRef = useRef(onUpdate);

    const { t } = useText();

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        dispatch({
            type: "HYDRATE",
            payload: stepData,
            fallbackMarkerPosition: {
                coordinates: location,
                doesMarkerEqualsLocation: false,
            },
        });
    }, [location, stepData]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!state.buildingInsights) {
            dispatch({
                type: "LOAD_ERROR",
                payload: "Die Dachanalyse ist noch nicht verfügbar.",
            });
            return;
        }

        trackSolarMapContinued(!state.markerPosition.doesMarkerEqualsLocation && state.markerPosition.coordinates !== location);
        onSubmit({
            buildingInsights: state.buildingInsights,
            markerPosition: state.markerPosition,
        });
    };

    useEffect(() => {
        const fetchBuildingInsights = (coords: Coordinates) => {
            dispatch({ type: "LOAD_START" });
            fetch(`${import.meta.env.VITE_BACKEND_BASE_URL!}/api/solar/fetchBuildInsights?latitude=${coords.lat}&longitude=${coords.lng}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to fetch Building Insights, you can still continue but there might be calculation issues");
                }

                return res.json();
            })
            .then((data: BuildingInsightsResponse) => {
                const nextStepData: SolarMapStepData = {
                    buildingInsights: data,
                    markerPosition: {
                        coordinates: { lat: data.center.latitude, lng: data.center.longitude },
                        doesMarkerEqualsLocation: false
                    },
                };
                if (data.name === `buildings/${address.prediction?.placeId}`) {
                    nextStepData.markerPosition = { ...nextStepData.markerPosition, doesMarkerEqualsLocation: true };
                }

                dispatch({ type: "LOAD_SUCCESS", payload: nextStepData });
                onUpdateRef.current(nextStepData);
            })
            .catch((err) => {
                dispatch({
                    type: "LOAD_ERROR",
                    payload: err instanceof Error ? err.message : "Fehler beim Laden der Gebäudedaten.",
                    severity: "warning",
                });
            });
        };

        if (location) {
            fetchBuildingInsights(location);
        }
    }, [location, address.prediction?.placeId]);

    return (
        <Form onSubmit={handleSubmit}>
            <Stack direction={{ sm: "row", xs: "column" }} spacing={2} sx={{
                alignItems: "center",
            }}>
                <Container disableGutters sx={{ minWidth: { sm: "50%", xs: "100%" } }}>
                    <SolarGoogleMap
                        location={location}
                        apiKey={import.meta.env.VITE_GOOGLE_API_KEY!}
                        markerPosition={state.markerPosition}
                        aspectRatio={"4 / 3"}
                        isDraggable={true}
                    />
                </Container>
                <Stack direction={"column"} spacing={2}>
                    <Typography variant={"h2"} align={"left"}>
                        {t('funnel.solarMapStep.title')}
                    </Typography>
                    <Typography variant={"body1"} align={"left"}>
                        {t('funnel.solarMapStep.description')}
                    </Typography>
                    <Button
                        variant={"contained"}
                        type={"submit"}
                        size={"large"}
                        loading={state.isLoading}
                        disabled={state.isLoading || !state.buildingInsights}
                        sx={{
                            borderRadius: '0.5rem',
                            px: "2.5rem",
                            py: "1rem",
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            inlineSize: 'fit-content',
                            whiteSpace: 'nowrap',
                        }}>Weiter</Button>
                    {state.error && <Alert severity={state.errorSeverity ?? "error"}><span dangerouslySetInnerHTML={{ __html: t('funnel.solarMapStep.error')! }} /></Alert>}
                </Stack>
            </Stack>
        </Form>
    );
};

export default SolarMapStep;
